import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PrototypeLogLine } from '@aiai/shared';
import { fetchPrototypeBuildLog } from '../api';
import { qk } from '../api/queryKeys';

/** How close to the bottom (px) still counts as "following" the output. */
const PIN_SLACK_PX = 24;

/**
 * Monospace view of a prototype's build output (npm install / vite build +
 * `[aiai]` deploy milestones). Auto-scrolls to the newest line while the user
 * is at the bottom; scrolling up stops following until they scroll back down.
 */
export function BuildOutputLog({
  lines,
  emptyMessage,
  className = 'h-64',
}: {
  lines: PrototypeLogLine[];
  emptyMessage: string;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [lines]);

  return (
    <div
      ref={scrollRef}
      data-testid="build-output"
      onScroll={(e) => {
        const el = e.currentTarget;
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_SLACK_PX;
      }}
      className={`${className} overflow-auto rounded-lg border border-line bg-surface-1 p-3 text-left font-mono text-xs leading-relaxed`}
    >
      {lines.length === 0 && <div className="text-ink-faint">{emptyMessage}</div>}
      {lines.map((l, i) => (
        <div
          key={i}
          className={`whitespace-pre-wrap break-words ${
            l.stream === 'stderr'
              ? 'text-bad'
              : l.text.startsWith('[aiai]')
                ? 'text-info'
                : 'text-ink-muted'
          }`}
        >
          {l.text}
        </div>
      ))}
    </div>
  );
}

/**
 * After a failed deploy: the last deploy's build output, so the real npm/vite
 * error sits next to the one-line `lastError`. Renders nothing when no build
 * output was recorded (e.g. the deploy failed before the build started).
 */
export function PrototypeFailedBuildOutput({ prototypeId }: { prototypeId: string }) {
  const query = useQuery({
    queryKey: qk.prototypeBuildLog(prototypeId),
    queryFn: () => fetchPrototypeBuildLog(prototypeId),
  });
  const lines = query.data ?? [];
  if (lines.length === 0) return null;
  return (
    <details open className="rounded-xl border border-line bg-surface-2 px-4 py-3">
      <summary className="cursor-pointer text-sm font-semibold text-ink">
        Last build output
        <span className="ml-2 text-xs font-normal text-ink-muted">{lines.length} lines</span>
      </summary>
      <div className="mt-3">
        <BuildOutputLog lines={lines} emptyMessage="No build output was recorded." className="h-80" />
      </div>
    </details>
  );
}
